<template lang="pug">
.v-field-select.field-select(:class="field.className, {'field-select--open': open}")
	label.field-select__label(v-if="field.label && field.mode !== 'no-label'" :for="uniqueId")
		| {{field.label}}
		span.field-text__star(v-if="field.required") *
	template(v-if="field.searchable || isDistrict")
		.field-select__field-text.field-text
			input.field-select__field-text-input.field-input(
					ref="input"
					type="text"
					:placeholder="field.placeholder"
					:inputmode="field.inputmode"
					:disabled="field.disabled"
					v-model="selectValueText"
					@input="open = true"
					@focus="inputFocus"
					@blur="inputBlur"
					:id="uniqueId"
					@keyup="foundSelectValue")
			button.field-select__shevron(type="button" @click="toggleOpen" :disabled="field.disabled")
				v-svg-icon.field-select__btn-arrow(icon="chevron-down-sm")
		.field-select__list.scrollbar(@click="open = false")
			.field-select__list-container
				template(v-for="(option, optionIndex) in displayOptions" :key="optionIndex")
					template(v-if="isFoundSelectText(option.text)")
						button.field-select__list-item(type="button" @click.stop="handleClickSearchable($event, option)" :disabled="field.disable") {{option.text}}
							v-svg-icon.field-select__check-list-icon(icon="check-icon")
				.field-select__list-item.field-select__list-item--message(v-if='selectValueText && selectValueText.length > 0 && !isSelectValueFound') Ничего не найдено
	template(v-else)
		button.field-select__btn(type="button" @click="open = !open" :id="uniqueId" :disabled="field.disabled")
			span.field-select__btn-placeholder(v-if="!activeOption && field.placeholder" key="placeholder") {{field.placeholder}}
			span.field-select__btn-text(v-else-if="activeOption" key="text") {{activeOption.text}}
			v-svg-icon.field-select__btn-arrow(icon="chevron-down-sm")
		.field-select__list.scrollbar
			.field-select__list-container
				template(v-if="field.checkbox" )
					template(v-for="(option, index) in field.options")
						label.field-select__list-item.field-select__list-item--flex(:for="uniqueId + index" :class="option?.className")
							input.field-select__input(
								type="checkbox"
								:id="uniqueId + index"
								:value="option.value"
								@change="selectOption(option.value)"
								:disabled="field.disabled || option.disabled")
							.field-checkbox__visual
							span.field-checkbox__text {{option.text}}
				template(v-else)
					button.field-select__list-item(type="button"
							v-for="(option, optionIndex) in field.options"
							@click="selectOption(option.value)"
							:key="optionIndex")
						| {{option.text}}
			button.btn.btn--primary.field-select__mobile-apply(
				v-if="showMobileApply"
				type="button"
				@click.stop="open = false") {{ field.mobileApplyText }}
</template>

<script>
import uniqueId from '../scripts/helpers/uniqueId.js';
import isMobMixin from '../components/mixins/v-mixin-is-mob.vue';

export default {
	mixins: [isMobMixin],

	props: {
		field: {
			type: Object,
			default: null,
			required: true
		},

		modelValue: {
			type: [Array, Object, String, Number, Boolean, Symbol, Date],
			default: null
		}
	},

	emits: [
		'update:model-value'
	],

	data() {
		return {
			localValue: null,
			open: false,
			uniqueId: uniqueId('field-select_'),
			hasFocus: false,
			selectValueText: '',
			isSelectValueFound: false,
		};
	},

	computed: {
		showMobileApply() {
			return Boolean(this.field?.mobileApplyText && this.isSm);
		},

		activeOption() {
			if (this.field && this.field.options && this.field.options.length) {
				return this.field.options.find(option => option.value === this.localValue);
			}
			return null;
		},

		// Поле District (FILTER[district]) — длинный список районов: принудительно
		// включаем поиск по вводу и сортировку опций по алфавиту на всех формах.
		isDistrict() {
			if (!this.field) return false;
			const id = String(this.field.id || '').split('_')[0];
			return this.field.code === 'district' || id === '789d3a3492c503ad75dc8c631c359129';
		},

		// Опции для отображения: для District — отсортированы по алфавиту, иначе как есть.
		displayOptions() {
			const options = (this.field && Array.isArray(this.field.options)) ? this.field.options : [];
			if (this.isDistrict) {
				return [...options].sort((a, b) =>
					String(a.text || '').localeCompare(String(b.text || ''), undefined, { sensitivity: 'base' }));
			}
			return options;
		}
	},

	watch: {
		modelValue() {
			this.updateLocalValue();
		}
	},

	created() {
		this.updateLocalValue();
		if ((this.field.searchable || this.isDistrict) && this.localValue) {
			this.selectValueText = this.activeOption ? this.activeOption.text || this.activeOption.value : '';
		}
		document.addEventListener('click', this.handleClickOutside);
		document.addEventListener('keydown', this.handleEsc);
	},

	unmounted() {
		document.removeEventListener('click', this.handleClickOutside);
		document.removeEventListener('keydown', this.handleEsc);
	},

	methods: {
		selectOption(value) {
			this.localValue = value;
			this.handleInput();
			if (!this.showMobileApply) {
				this.open = false;
			}
		},

		handleClickSearchable(e, option) {
			this.localValue = option.value;
			this.handleInput();
			this.open = false;
			this.selectValueText = option.text;
		},

		cleanField() {
			if (this.foundSelectValue() !== this.selectValueText) {
				this.selectValueText = '';
				this.localValue = '';
			}
		},

		toggleOpen() {
			if (this.field.disabled) {
				return false;
			}
			this.open = !this.open;
			this.selectValueText = '';
		},

		foundSelectValue() {
			let result = false;
			if (this.field && this.field.options && this.field.options.length) {
				for (let i = 0; i < this.field.options.length; i++) {
					if (this.isFoundSelectText(this.field.options[i].text)) {
						result = this.field.options[i].text;
						this.isSelectValueFound = true;
						break;
					}
				}
				if (result) {
					this.isSelectValueFound = true;
				} else {
					this.isSelectValueFound = false;
				}
			}
			console.log(result)
			return result;
		},

		isFoundSelectText(text = '') {
			let vm = this;
			let result = false;
			let selectValueText = vm.selectValueText;
			if (!selectValueText) {
				selectValueText = '';
			}
			if (text.toLowerCase().indexOf(selectValueText.toLowerCase()) !== -1) {
				result = true;
			} else {
				result = false;
			}
			return result;
		},

		inputFocus() {
			this.hasFocus = true;
			// при установке курсора в поле сразу раскрываем список
			this.open = true;
		},

		inputBlur() {
			this.hasFocus = false;
		},

		updateLocalValue() {
			this.localValue = this.modelValue;
		},

		handleInput() {
			this.$emit('update:model-value', this.localValue);
		},

		handleClickOutside(e) {
			if (!this.$el.contains(e.target)) {
				this.open = false;
			}
		},

		handleEsc(e) {
			if (e.keyCode === 27 && this.open) {
				e.stopPropagation();
				this.open = false;
			}
		}
	}
};
</script>
